// Evidence-aware reply drafting, the client's half (busy-ai-srv#13, AI Brain spec §8).
//
// Asking Busy to reply inside an Ongoing now opens the thread in the centre and fills the composer
// with a draft the BFF wrote from SCOPED evidence: mail from the same story, its tasks, notes and
// documents, its research findings. The draft says which of those it used, and this module is where
// "what the user is shown about that" is decided — the pill over the composer, the Sources rows on
// the Work-board, where each of them opens, and the Briefing's line.
//
// Pure, no store and no Vue: the sources panel is the part of this feature that is easiest to get
// quietly wrong (a source that opens the wrong screen, a count that includes what the draft ignored)
// and it has to be checkable without mounting the box.

/** Where the centre goes when a reply is drafted: the thread itself, told that a reply is coming. */
export function composeRoute(timelineId) {
  return { path: `/emails/timeline/${timelineId}`, query: { compose: 'reply' } }
}

/** True when a route was opened BY the reply gesture — the thread view scrolls to the last message. */
export function wantsReplyCompose(query) {
  return String(query?.compose || '') === 'reply'
}

/**
 * A reference as the services write it — "task:<guid>@busy-task-srv" — back into its parts.
 *
 * Research findings are cited as "finding:<guid>": they have no object type of their own (the same
 * gap lib/objectRef.js records), but several findings out of one pack must stay several sources, so
 * they are cited by their own id and carry an `openRef` for the screen that can actually show them.
 */
export function parseEvidenceRef(ref) {
  const text = String(ref || '').trim()
  if (!text) return null
  const at = text.lastIndexOf('@')
  const body = at >= 0 ? text.slice(0, at) : text
  const colon = body.indexOf(':')
  if (colon <= 0) return null
  const type = body.slice(0, colon).replace(/[-_\s]/g, '').toLowerCase()
  const id = body.slice(colon + 1).trim()
  if (!type || !id) return null
  return { type, id }
}

/**
 * What clicking a source opens.
 *
 *   • a story or a thread is a PLACE — a route;
 *   • a task, note or document is opened in the box, the way every other row of the app opens one
 *     (the caller dispatches its own open mode; this only says which and with what id);
 *   • a research pack lives inside its story, so it needs the story's id to be reachable at all —
 *     without one the story itself is the honest destination.
 *
 * Null when the source names nothing openable, so a caller can render the row as plain text rather
 * than as a button that does nothing.
 */
export function evidenceTarget(item, { ongoingId = null } = {}) {
  const parsed = parseEvidenceRef(item?.openRef || item?.ref)
  if (!parsed) return null
  const { type, id } = parsed
  if (type === 'timeline') return { kind: 'route', path: `/emails/timeline/${id}` }
  if (type === 'ongoing') return { kind: 'route', path: `/ongoing/${id}` }
  if (type === 'researchpack') {
    if (!ongoingId) return null
    return { kind: 'route', path: `/ongoing/${ongoingId}`, query: { pack: id } }
  }
  if (type === 'task' || type === 'note' || type === 'document') return { kind: 'open', type, id }
  return null
}

/** The glyph a source wears — the module's own, so a task on the board and a task here match. */
const ICONS = {
  email: 'email',
  timeline: 'email',
  task: 'tick-circle',
  note: 'notes',
  document: 'file-doc',
  finding: 'research-pack',
  researchpack: 'research-pack',
  ongoing: 'story',
}

export function evidenceIcon(type) {
  return ICONS[String(type || '').toLowerCase()] || 'file-doc'
}

/**
 * The BFF's evidence list as Work-board rows.
 *
 * `kind: 'evidence'` keeps them out of the SELECTION: nothing here was ticked by the user, and a
 * Save or a Clear must not treat the draft's sources as things in hand (the same rule an opened
 * research pack follows). The id is derived from the ref, so re-drafting the same thread replaces
 * its rows instead of stacking a second copy of each.
 */
export function evidenceBoardItems(evidence) {
  return (evidence || [])
    .filter(e => e && e.ref)
    .map(e => ({
      id: `evidence:${e.ref}`,
      kind: 'evidence',
      ref: String(e.ref),
      openRef: e.openRef ? String(e.openRef) : null,
      type: String(e.type || 'item'),
      title: String(e.title || 'Source'),
      snippet: String(e.snippet || ''),
      used: !!e.used,
      // How sure the service is that this is the right file — carried through untouched for the
      // Work-board's "Found documents" badge (busy-my-web#416, lib/foundDocuments.js). Null rather
      // than 0 when it is absent, which it is today: a missing confidence is not a zero one.
      confidence: e.confidence ?? null,
      icon: evidenceIcon(e.type),
      timelineId: e.timelineId || null,
    }))
}

/** The sources the draft actually used — what the pill counts. */
export function usedEvidence(evidence) {
  return (evidence || []).filter(e => e && e.used)
}

/**
 * "Drafted from 3 sources" — the small pill over the composer.
 *
 * Null when the draft used none: a pill claiming "0 sources" over a reply written from the thread
 * alone says the feature ran and found nothing, which is not what happened — the thread IS what it
 * was written from.
 */
export function draftedFromLabel(evidence) {
  const n = usedEvidence(evidence).length
  if (!n) return null
  return `Drafted from ${n} source${n === 1 ? '' : 's'}`
}

/** "Draft based on: Kitchen quote · 3 sources" — the Briefing's row while a draft is in the box. */
export function draftBriefingText(draft) {
  // Explicitly null is the everyday case (no draft in the box), so it is read rather than
  // destructured — a default parameter does not catch a null that was passed on purpose.
  const name = String(draft?.subject || '').trim()
  if (!name) return null
  const n = usedEvidence(draft?.evidence).length
  return n ? `Draft based on: ${name} · ${n} source${n === 1 ? '' : 's'}` : `Draft based on: ${name}`
}

/**
 * "Replying on Kitchen quote · you asked about “supplier”" — the Briefing's line when the THREAD was
 * chosen for the user (busy-main-bff#25, spec §8: "the user must be able to inspect the sources").
 *
 * Only when something was decided: a reply started from the thread's own Reply chip picked nothing,
 * so there is nothing to justify and the ordinary "Draft based on:" line says it all. Null keeps the
 * row out of the strip entirely rather than drawing a half sentence.
 */
export function replyPickText(draft) {
  const why = String(draft?.pickedWhy || '').trim()
  if (!why) return null
  const subject = String(draft?.subject || '').trim()
  return subject ? `Replying on ${subject} · ${why}` : `Replying on this thread · ${why}`
}

/**
 * The other threads, as the "which email?" picker takes them — `{ timelineId, subject, from }`. The
 * SAME shape the story's Reply chip builds, so switching threads goes down the one path that already
 * knows how to compose on one.
 */
export function switchThreadOptions(draft) {
  return (draft?.alternatives || [])
    .filter(a => a && a.timelineId)
    .map(a => ({
      timelineId: String(a.timelineId),
      subject: String(a.subject || 'Email'),
      from: String(a.sender || ''),
    }))
}

/**
 * The thread choice, carried across a manual switch (busy-main-bff#25).
 *
 * Switching threads from the Briefing re-drafts by NAMING the thread, so the service picks nothing
 * and answers without a reason or a list — which would take the "Switch thread" control away the
 * first time it was used, exactly when the user is still hunting for the right conversation. So the
 * choice is remembered on this side: the reason becomes the user's own, and the alternatives are the
 * previous ones plus the thread just left, minus the one now being replied on.
 *
 * Only ever ADDS to a draft the service did not decide. A picked draft is returned untouched.
 */
export function withCarriedPick(draft, previous) {
  if (!draft || draft.pickedWhy) return draft
  const picked = String(draft.pickedTimelineId || draft.thread?.timelineId || '')
  const left = previous?.thread?.timelineId
    ? [{ timelineId: previous.thread.timelineId, subject: previous.thread.subject || 'Email', sender: '' }]
    : []
  const seen = new Set()
  const alternatives = [...(previous?.alternatives || []), ...left]
    .filter(a => a && a.timelineId && String(a.timelineId) !== picked)
    .filter((a) => {
      const id = String(a.timelineId)
      if (seen.has(id)) return false
      seen.add(id)
      return true
    })
  if (!alternatives.length) return draft
  return { ...draft, pickedWhy: 'you chose this thread', alternatives }
}

/** The documents among the sources — the ones that can be attached to the reply as chips. */
export function attachableDocuments(evidence) {
  return (evidence || []).filter(e => e && String(e.type).toLowerCase() === 'document' && e.ref)
}

/**
 * A document source as the board's existing FILE chip (kind 'doc'), so ticking one puts it in the
 * same row of chips an uploaded attachment lands in — no second kind of attachment to keep in step.
 */
export function documentChip(item) {
  const parsed = parseEvidenceRef(item?.ref)
  if (!parsed || parsed.type !== 'document') return null
  return {
    id: `doc:${parsed.id}`,
    kind: 'doc',
    documentId: parsed.id,
    title: String(item.title || 'Document'),
    name: String(item.title || 'Document'),
  }
}

/**
 * The body the composer is filled with. The quick box holds PLAIN text (it re-wraps into paragraphs
 * on send, exactly as a note or task body does), so the draft's own text twin is preferred and the
 * HTML is only unwrapped when the service sent nothing else.
 */
export function draftBodyText(draft) {
  const text = String(draft?.bodyText || '').trim()
  if (text) return text
  const html = String(draft?.bodyHtml || '')
  if (!html) return ''
  return html
    .replace(/<\s*(br|\/p|\/div|\/li)\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean)
    .join('\n')
    .trim()
}
